import { CloseIcon } from '@chakra-ui/icons';
import type { DateTime } from 'luxon';
import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useIsBreakpoint } from '@/hooks/useIsBreakpoint';
import { TimeInput as TimeInputField } from '@/pages/Schedule/components';
import { Button, Text, IconButton, useUpdateEffect, usePrevious } from '@/ui';
import { parseTime } from '@/utils/parseTime';
import { EntryModalFieldButton } from '../../EntryModalFieldButton';

type Props = {
  startDate: DateTime;
  endDate: DateTime;
  isOnDay: boolean;
  hasRecurrence: boolean;
  onAddTimeClick: () => void;
  onRemoveTimeClick: () => void;
  onStartTimeChange: (time: DateTime) => void;
  onEndTimeChange: (time: DateTime) => void;
  onClose: () => void;
};

const toFormat = (date: DateTime) => date.toFormat('HH:mm');

export const TimeInput = ({
  startDate,
  endDate,
  isOnDay,
  onAddTimeClick,
  onRemoveTimeClick,
  onStartTimeChange,
  onEndTimeChange,
  onClose,
}: Props) => {
  const { t } = useTranslation('entryModal');
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);
  const isSmBreakpoint = useIsBreakpoint('sm');

  const [startTime, setStartTime] = useState(() => toFormat(startDate));
  useEffect(() => {
    setStartTime(toFormat(startDate));
  }, [startDate]);

  const [endTime, setEndTime] = useState(() => toFormat(endDate));
  useEffect(() => {
    setEndTime(toFormat(endDate));
  }, [endDate]);

  const handleStartTimeChange: ChangeEventHandler<HTMLInputElement> = (event) =>
    setStartTime(event.currentTarget.value);

  const handleEndTimeChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setEndTime(event.currentTarget.value);
  };

  const handleStartTimeBlur: FocusEventHandler<HTMLInputElement> = () => {
    const newStartTime = parseTime(startTime);

    if (newStartTime) {
      onStartTimeChange(newStartTime);
    } else {
      setStartTime(toFormat(startDate));
    }
  };

  const handleEndTimeBlur: FocusEventHandler<HTMLInputElement> = () => {
    const newEndTime = parseTime(endTime);

    if (newEndTime) {
      onEndTimeChange(newEndTime);
    } else {
      setEndTime(toFormat(endDate));
    }
  };

  const handleStartTimeKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      endInputRef.current?.focus();
    }
  };

  const handleEndTimeKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
      onClose();
    }
  };

  const prevIsOnDay = usePrevious(isOnDay);
  useUpdateEffect(() => {
    if (prevIsOnDay && !isOnDay) {
      // timeout is required for tests
      setTimeout(() => startInputRef.current?.select());
    }
  }, [isOnDay, prevIsOnDay]);

  if (isOnDay) {
    return (
      <EntryModalFieldButton
        placeholder={t('time.add')}
        onClick={onAddTimeClick}
      />
    );
  }

  return (
    <>
      <TimeInputField
        aria-label={t('time.aria.start')}
        autoFocus
        flex="0"
        minW="125px"
        ref={startInputRef}
        required
        value={startTime}
        variant="notion"
        onBlur={handleStartTimeBlur}
        onChange={handleStartTimeChange}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={handleStartTimeKeyDown}
      />
      <Text variant="light">&ndash;</Text>
      <TimeInputField
        aria-label={t('time.aria.end')}
        flex="0"
        max="24:00"
        min={startTime}
        minW="125px"
        ref={endInputRef}
        required
        value={endTime}
        variant="notion"
        onBlur={handleEndTimeBlur}
        onChange={handleEndTimeChange}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={handleEndTimeKeyDown}
      />

      {isSmBreakpoint ? (
        <IconButton
          aria-label={t('time.remove')}
          fontSize="xs"
          icon={<CloseIcon boxSize={2} />}
          ml="2"
          size="xs"
          variant="secondary"
          onClick={onRemoveTimeClick}
        />
      ) : (
        <Button
          color="customgray.mid"
          colorScheme="dark"
          size="xs"
          variant="link"
          onClick={onRemoveTimeClick}
        >
          {t('time.remove')}
        </Button>
      )}
    </>
  );
};
